import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function StHelensCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="St Helens Pothole Claims | 75% of B/C Roads Amber or Red | Fixtyer"
        description="St Helens Borough Council earns GREEN on spend but AMBER overall — 75% of B and C roads are amber or red, pothole repairs nearly tripled in three years, and the council admits amber condition is the highest deterioration risk. See what that means for your claim."
        canonical="/councils/st-helens"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'St Helens', url: '/councils/st-helens' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'St Helens matches its DfT capital allocation every year — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but the ratings that matter for your claim are road condition (AMBER) and best practice (also AMBER). Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on whether capital spend equals the DfT allocation. St Helens\'s own condition data shows 75% of B and C roads in amber or red condition, and the council states that rising amber levels "indicate the highest risk and potential for more rapid deterioration into red."',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads sit within a combined 607km C-and-U figure on a 733km network — but the council does not publish a separate U-road length. It publishes only RED-condition percentages for U-roads: 12% in 2021 rising to 14% in 2024, with no amber or green breakdown. That means at least one in seven unclassified roads is officially in RED (should be considered for maintenance), and the council cannot show from published data what share of the remainder is in amber (maintenance may be required soon).',
          },
          {
            question: 'Does the council admitting amber roads are the highest risk help my claim?',
            answer: 'Yes. St Helens states that while red condition has increased slightly, "it is the high increase in the levels of amber carriageway condition that indicate the highest risk and potential for more rapid deterioration into red." On B and C roads, amber rose from 60% to 69% between 2021 and 2023 before easing to 64% in 2024 — still nearly two-thirds of the classified local road network. That is documented knowledge that large parts of the network are deteriorating toward failure.',
          },
          {
            question: 'Pothole repairs peaked at 2,369 in 2023/24 — does that mean the roads are fixed?',
            answer: 'No. St Helens filled 7,807 potholes over five years while U-road RED condition rose from 12% to 14% and B/C roads in green condition fell from 32% to 25%. The council projects circa 2,000 pothole repairs for 2025/26 and states that reactive maintenance "does not offer value for money" — acknowledging that patch-and-fill does not fix underlying deterioration.',
          },
          {
            question: 'St Helens surveys its network annually with AI video cameras — does that weaken my claim?',
            answer: 'It strengthens the council\'s Section 58 paperwork, but the survey data itself creates pressure points. The council conducts full-network video surveys each year using 360-degree cameras and AI scoring — so it cannot claim ignorance of network condition. If your road was in amber or red at the last survey and no planned maintenance followed, that gap between known condition and action is exactly what a specific claim should target. Prior reports and photos of the individual defect remain decisive.',
          },
        ]}
      />
      <ServiceSchema
        councilName="St Helens Borough Council"
        councilSlug="st-helens"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/st-helens"
        headline="St Helens Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">St Helens</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            St Helens: 75% of B/C Roads in Amber or RED
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            St Helens Borough Council spends every penny of its DfT capital allocation and earns a{' '}
            <strong className="text-emerald-400">GREEN</strong> spend scorecard. Yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> — because{' '}
            <strong className="text-rose-400">75% of B and C roads are now amber or red</strong>, pothole
            repairs nearly tripled in three years, and the council itself warns that rising amber condition
            is &ldquo;the highest risk&rdquo; of rapid deterioration.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">7,807</div>
            <div className="text-white font-medium mt-2">Potholes filled in five years</div>
            <div className="text-brand-300 text-sm mt-1">
              From 808 in 2020/21 to a peak of 2,369 in 2023/24 — while U-road RED condition climbed from
              12% to 14% across St Helens&apos;s 733km network.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Four years of AI-assisted video survey data from St Helens&apos;s own transparency report —
            classified roads slipping, residential RED condition edging up
          </p>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">Network size (733km total carriageway)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div className="text-center bg-white rounded-lg p-3 border border-surface-200">
                <div className="text-2xl font-bold text-brand-900">85km</div>
                <div className="text-surface-600">A roads</div>
              </div>
              <div className="text-center bg-white rounded-lg p-3 border border-surface-200">
                <div className="text-2xl font-bold text-brand-900">41km</div>
                <div className="text-surface-600">B roads</div>
              </div>
              <div className="text-center bg-white rounded-lg p-3 border border-surface-200">
                <div className="text-2xl font-bold text-brand-900">607km</div>
                <div className="text-surface-600">C and U roads</div>
              </div>
              <div className="text-center bg-white rounded-lg p-3 border border-surface-200">
                <div className="text-2xl font-bold text-brand-900">20%</div>
                <div className="text-surface-600">Classified share</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm mt-4">
              The council notes that A, B and C roads make up the classified network carrying most journeys.
              The remaining unclassified roads are residential in nature — alongside C roads in the combined
              607km figure. Footways add a further 1,158km; cycleways 86km.
            </p>
          </div>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (85km): declining green share</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100 bg-surface-50">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No data — COVID-19</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">41%</td>
                    <td className="px-4 py-2 text-center">54%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">46%</td>
                    <td className="px-4 py-2 text-center">49%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">51%</td>
                    <td className="px-4 py-2 text-center">43%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">53%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">41%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED is broadly flat at 5–6%, but green roads have fallen from 54% to 41%. More than half
              of St Helens&apos;s main roads are now amber.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads: deteriorating</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">8%</td>
                    <td className="px-4 py-2 text-center">60%</td>
                    <td className="px-4 py-2 text-center">32%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">9%</td>
                    <td className="px-4 py-2 text-center">66%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">11%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">69%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">20%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">11%</td>
                    <td className="px-4 py-2 text-center font-bold">64%</td>
                    <td className="px-4 py-2 text-center font-bold">25%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B/C roads in RED rose from 8% to 11%. Green roads halved from 32% to 20% before a partial
              recovery to 25%. In 2024, 75% of B and C roads were amber or red combined.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Fully Funded — But Only 0.2% of the Network Each Year</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£8.236m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£8.236m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">34%</div>
                <div className="text-brand-300 text-sm">Projected reactive maintenance share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              St Helens spends its full DfT allocation every year — capital spend has matched allocation
              exactly since 2020/21. Yet the council states funding is{' '}
              <span className="text-amber-400">only sufficient to maintain 0.2% of the network each year</span>.
              One-third of spend is still reactive, and approximately 14% of that reactive spend goes on
              pothole repairs alone.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Unclassified Roads: RED Only
          </h2>
          <p className="text-surface-600 mb-8">
            U-road condition published as RED percentages only — no amber or green breakdown
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Amber / Green</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100 bg-surface-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">No data collected</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">COVID-19</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not published</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not published</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not published</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not published</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The RED-Only Gap</h3>
              <p className="text-surface-600 text-sm mb-3">
                St Helens publishes full red/amber/green breakdowns for A, B and C roads, but U-roads list
                only the RED percentage — even though the council conducts full-network video surveys annually.
              </p>
              <p className="text-surface-600 text-sm">
                At 14% RED, at least <strong>one in seven unclassified roads is officially flagged for maintenance</strong>.
                The council does not publish U-road length separately from C roads (607km combined), so a precise
                kilometre figure cannot be calculated from the report — but the percentage has risen every year
                since surveys resumed in 2021.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Annual AI Surveys</h3>
              <p className="text-amber-800 text-sm mb-3">
                Since 2021, St Helens has conducted a full network video survey using vehicles with 360-degree
                high-definition cameras. The imagery identifies and records 35 types of road defect, interpreted
                by AI to produce condition scores.
              </p>
              <p className="text-amber-800 text-sm">
                From 2026/27, a new PAS 2161 methodology will replace the current three-category system. Surveys
                were not conducted in 2020 due to COVID-19 restrictions.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 41 of the Highways Act 1980, St Helens must maintain its roads. Section 58 lets
              it defend claims by proving reasonable inspection and repair systems. For St Helens, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road surveyed in the latest annual AI video assessment — and what category was it?</li>
              <li>• If 14% of U-roads are RED and 64% of B/C roads are amber, what planned works followed for your street?</li>
              <li>• The council admits amber is the highest risk of rapid decline — was your defect on an amber road left untreated?</li>
              <li>• With only 0.2% of the network maintainable per year, how does the council prioritise which defects get fixed?</li>
            </ul>
            <p className="text-white font-medium">
              Annual surveys mean the council cannot plead ignorance — only that it chose not to act on what
              it already knows.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            7,807 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes that nearly tripled — then stayed elevated
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Conventional resurfacing (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Preventative treatments (m²)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">808</td>
                  <td className="px-6 py-4 text-center">10</td>
                  <td className="px-6 py-4 text-center">0</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">912</td>
                  <td className="px-6 py-4 text-center">8</td>
                  <td className="px-6 py-4 text-center">0</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,698</td>
                  <td className="px-6 py-4 text-center">11</td>
                  <td className="px-6 py-4 text-center">0</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,369</td>
                  <td className="px-6 py-4 text-center">6</td>
                  <td className="px-6 py-4 text-center">7,000</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">2,020</td>
                  <td className="px-6 py-4 text-center">6</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">26,000</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">7,807</td>
                  <td className="px-6 py-4 text-center">—</td>
                  <td className="px-6 py-4 text-center">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Nearly Triple in Three Years</h3>
              <p className="text-surface-600 text-sm">
                Pothole repairs rose from 808 in 2020/21 to 2,369 in 2023/24 — a 193% increase in three
                years. Even after falling to 2,020 in 2024/25, the council projects circa 2,000 pothole
                repairs for 2025/26. Reactive volume and worsening survey data can coexist.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Multihog Patch Cycle</h3>
              <p className="text-amber-800 text-sm">
                Most road repairs are undertaken by the council&apos;s in-house team using a Multihog vehicle
                with a 400mm planer for permanent first-time fixing. The council states reactive maintenance
                &ldquo;does not offer value for money&rdquo; and that prevention is better than cure. Patching
                proves defects existed; it does not prove the network was proactively maintained.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Amber Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Council&apos;s Own Amber Warning
          </h2>
          <p className="text-surface-600 mb-8">
            St Helens publicly identifies rising amber condition as the highest deterioration risk
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Further analysis of the road network by road class over the previous five years is showing a slight increase in 'red' condition, however, it is the high increase in the levels of 'amber' carriageway condition that indicate the highest risk and potential for more rapid deterioration into 'red'."
              source="St Helens Borough Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Reactive maintenance does not offer value for money to residents, and the council believe that the overall quality and safety of the network will improve if a more preventative approach to maintenance is adopted. The funding received is only sufficient to maintain 0.2% of the network each year, therefore the council has adopted the principle that 'prevention is better than cure' in determining the balance between preventative, planned, and reactive maintenance activities."
              source="St Helens Borough Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Every pound that we invest in preventative maintenance has the potential to save between six and ten pounds in rebuilding costs later."
              source="St Helens Borough Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2025/26 Forward Plan</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Conventional resurfacing: 17km (up from 6km in 2024/25)</li>
                <li>• Surface dressing (preventative): 4km</li>
                <li>• Footway maintenance: 7km</li>
                <li>• LED street lighting replacements: 1,159 units</li>
                <li>• Estimated pothole repairs: circa 2,000</li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">What This Admission Means</h3>
              <p className="text-amber-800 text-sm">
                St Helens formally acknowledges that amber roads represent the highest risk of rapid failure.
                The council also admits its funding can only maintain 0.2% of the 733km network annually — while
                17km of planned resurfacing in 2025/26 is 2.3% of the network, still a fraction of roads in
                amber or red condition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending deep dive */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Spending: GREEN Scorecard, AMBER Best Practice
          </h2>
          <p className="text-surface-600 mb-8">
            Capital spend matches DfT allocation exactly — preventative share still building
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-amber-700">Planned</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£4,894</td>
                  <td className="px-4 py-3 text-center">£4,894</td>
                  <td className="px-4 py-3 text-center">£2,085</td>
                  <td className="px-4 py-3 text-center">0%</td>
                  <td className="px-4 py-3 text-center">73%</td>
                  <td className="px-4 py-3 text-center">27%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£2,984</td>
                  <td className="px-4 py-3 text-center">£2,984</td>
                  <td className="px-4 py-3 text-center">£2,063</td>
                  <td className="px-4 py-3 text-center">0%</td>
                  <td className="px-4 py-3 text-center">77%</td>
                  <td className="px-4 py-3 text-center">23%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£6,754</td>
                  <td className="px-4 py-3 text-center">£6,754</td>
                  <td className="px-4 py-3 text-center">£2,142</td>
                  <td className="px-4 py-3 text-center">0%</td>
                  <td className="px-4 py-3 text-center">75%</td>
                  <td className="px-4 py-3 text-center">25%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£7,919</td>
                  <td className="px-4 py-3 text-center">£7,919</td>
                  <td className="px-4 py-3 text-center">£1,988</td>
                  <td className="px-4 py-3 text-center">12%</td>
                  <td className="px-4 py-3 text-center">58%</td>
                  <td className="px-4 py-3 text-center">30%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£7,394</td>
                  <td className="px-4 py-3 text-center">£7,394</td>
                  <td className="px-4 py-3 text-center">£2,048</td>
                  <td className="px-4 py-3 text-center">19%</td>
                  <td className="px-4 py-3 text-center">47%</td>
                  <td className="px-4 py-3 text-center">34%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center font-bold">£8,236</td>
                  <td className="px-4 py-3 text-center font-bold">£8,236</td>
                  <td className="px-4 py-3 text-center font-bold">£2,431</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">15%</td>
                  <td className="px-4 py-3 text-center font-bold">51%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">34%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Spend AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: St Helens invests its full allocation — here&apos;s how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council&apos;s Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend — capital spend matches DfT allocation every year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Full-network AI video surveys conducted annually since 2021
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> In-house Multihog repair team for permanent first-time fixes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Preventative programme scaling up — 26,000m² surface dressing in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented highways infrastructure asset management strategy
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence backed by annual survey data. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 75% of B/C roads amber or red in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits amber is the highest deterioration risk
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road RED rose from 12% to 14% — amber/green not published
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 7,807 potholes filled in five years while condition worsened
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Funding maintains only 0.2% of network per year — council&apos;s own figure
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — reactive share still 34%
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and annual AI surveys, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect and the gap between known condition and action</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council online reports) — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous Multihog patching)</li>
              <li>• The road&apos;s class — B/C amber rates above 64% make classified-road deterioration hard to dispute</li>
              <li>• Whether planned works in the 2025/26 programme included your road</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites St Helens&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in St Helens?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A council with annual AI surveys demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No amber-risk admission cited</li>
                <li>• No prior-report search</li>
                <li>• No 0.2% maintenance-rate argument</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C road amber surge documented</li>
                <li>✅ Council&apos;s own amber-risk warning cited</li>
                <li>✅ 7,807 potholes in five years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to St Helens</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=st-helens"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My St Helens Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                St Helens matches its DfT capital allocation every year — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but the ratings that matter for your claim are road
                condition (AMBER) and best practice (also AMBER). Section 58 turns on whether the specific
                defect that damaged your vehicle was reasonably inspected and repaired — not on whether
                capital spend equals the DfT allocation. St Helens&apos;s own condition data shows 75% of B and
                C roads in amber or red condition, and the council states that rising amber levels
                &ldquo;indicate the highest risk and potential for more rapid deterioration into red.&rdquo;
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads sit within a combined 607km C-and-U figure on a 733km network — but the council
                does not publish a separate U-road length. It publishes only RED-condition percentages for
                U-roads: 12% in 2021 rising to 14% in 2024, with no amber or green breakdown. That means at
                least one in seven unclassified roads is officially in RED (should be considered for
                maintenance), and the council cannot show from published data what share of the remainder is
                in amber (maintenance may be required soon).
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the council admitting amber roads are the highest risk help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. St Helens states that while red condition has increased slightly, &ldquo;it is the high
                increase in the levels of &apos;amber&apos; carriageway condition that indicate the highest risk and
                potential for more rapid deterioration into &apos;red&apos;.&rdquo; On B and C roads, amber rose from
                60% to 69% between 2021 and 2023 before easing to 64% in 2024 — still nearly two-thirds of
                the classified local road network.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs peaked at 2,369 in 2023/24 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. St Helens filled 7,807 potholes over five years while U-road RED condition rose from 12%
                to 14% and B/C roads in green condition fell from 32% to 25%. The council projects circa 2,000
                pothole repairs for 2025/26 and states that reactive maintenance &ldquo;does not offer value
                for money&rdquo; — acknowledging that patch-and-fill does not fix underlying deterioration.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                St Helens surveys its network annually with AI video cameras — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It strengthens the council&apos;s Section 58 paperwork, but the survey data itself creates
                pressure points. The council conducts full-network video surveys each year using 360-degree
                cameras and AI scoring — so it cannot claim ignorance of network condition. If your road was
                in amber or red at the last survey and no planned maintenance followed, that gap between
                known condition and action is exactly what a specific claim should target. Prior reports and
                photos of the individual defect remain decisive.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.sthelens.gov.uk/article/13680/Local-Highways-Maintenance-Funding"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              St Helens Borough Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
